import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { safeReturnUrl } from '../utility/return-url';
import { AnalyticsService } from './analytics.service';
import { FirebaseService } from './firebase.service';
import { StoreService } from './store.service';
import { UserService } from './user.service';
import { CultureService } from '../culture/culture.service';

/**
 * Google sign-in, end to end: get a token from Firebase, exchange it for this app's own
 * JWT, and land the person on the site signed in.
 *
 * It lives here rather than in the login page because the popup is not always available.
 * When it is blocked - Facebook's and Instagram's in-app browsers, webviews, popup
 * blockers - the flow leaves the page entirely and comes back on a fresh load, by which
 * point the login component that started it no longer exists. Both halves have to share
 * one implementation or the redirect half quietly does nothing.
 *
 * It talks to the API directly rather than through AccountService, which belongs to the
 * lazily-loaded account module - a shared service must not depend on it.
 */
@Injectable({
    providedIn: 'root',
})
export class GoogleAuthService {

    /// The locale hints the login form had when the redirect started. They are only used
    /// if the account is being created for the first time, and the form is long gone by
    /// the time the browser comes back, so they travel in sessionStorage - and so does
    /// the page to go back to once signed in (the login page's ?returnUrl=).
    private static readonly HintsKey = 'izz_google_hints';

    private readonly isBrowser: boolean;

    constructor(
        private firebase: FirebaseService,
        private http: HttpClient,
        private userService: UserService,
        private store: StoreService,
        private toastr: ToastrService,
        private router: Router,
        private analytics: AnalyticsService,
        private culture: CultureService,
        @Inject(PLATFORM_ID) platformId: Object) {
        this.isBrowser = isPlatformBrowser(platformId);
    }

    /**
     * Starts sign-in. Resolves true when the caller should drop its loading state, and
     * false when the browser is navigating to Google and nothing more should happen here.
     */
    async signIn(country?: number, countryCode?: string, language?: number, returnUrl?: string): Promise<boolean> {
        if (!this.isBrowser) { return true; }

        // Callers that have a country form pass its value; the header dropdown has no
        // form at all, so fall back to the country the site already detected rather
        // than filing every one of those sign-ups under the US default.
        if (!country || !countryCode) {
            const detected = this.detectedCountry();
            country = country || detected.country;
            countryCode = countryCode || detected.countryCode;
        }

        this.rememberHints(country, countryCode, language || 22, returnUrl);

        let attempt;
        try {
            attempt = await this.firebase.signInWithGoogle();
        } catch {
            this.toastr.error(this.culture.t('google-sign-in-not-started', 'Google sign-in could not be started. Please try again.'));
            return true;
        }

        // Leaving for Google. Do not clear the caller's loading state - the page is
        // about to be replaced, and flipping it back just makes the button flicker.
        if (attempt.redirecting) { return false; }

        // The popup was closed. A decision, not a failure.
        if (attempt.cancelled || !attempt.idToken) { return true; }

        await this.exchange(attempt.idToken);
        return true;
    }

    /**
     * Called once on startup. Does nothing - and loads nothing - unless a redirect
     * sign-in is actually in flight.
     */
    async completeRedirectIfAny(): Promise<void> {
        if (!this.isBrowser || !this.firebase.hasPendingRedirect) { return; }

        const idToken = await this.firebase.completeGoogleRedirect();
        if (!idToken) { return; }

        await this.exchange(idToken);
    }

    /// Trades the Firebase token for this app's JWT and signs the person in.
    private async exchange(idToken: string): Promise<void> {
        const hints = this.readHints();

        try {
            const res: any = await this.http.post(environment.apiUrl + '/api/Account/GoogleSignIn', {
                IdToken: idToken,
                Country: hints.country,
                CountryCode: hints.countryCode,
                Language: hints.language
            }).toPromise();

            if (res === 1) {
                this.toastr.error(this.culture.t('google-sign-in-not-verified', 'We could not verify that Google sign-in. Please try again.'));
            } else if (res === 2) {
                this.toastr.error(this.culture.t('account-deactivated-contact-support', 'Your account has been deactivated, please contact support!'));
            } else if (res === 6) {
                this.toastr.error(this.culture.t('google-email-not-verified', 'That Google account has not verified its email address.'));
            } else if (res === null || res === undefined) {
                this.toastr.error(this.culture.t('something-went-wrong', 'Something went wrong, try again!'));
            } else {
                this.signedIn(res, hints.returnUrl);
            }
        } catch (error) {
            // The server sends a short reference on a real failure and writes the same
            // one to its log, so a support report can be traced to one line.
            const reference = error && error.error && error.error.reference;
            this.toastr.error(reference
                ? this.culture.t('something-went-wrong-reference', 'Something went wrong. Reference: {reference}', { reference })
                : this.culture.t('something-went-wrong', 'Something went wrong, try again!'));
        }
    }

    /// Same landing as a password login: store the token, load the profile, then reload
    /// so every component picks up the session - on the page asked for, else the home page.
    private signedIn(token: string, returnUrl?: string): void {
        // A login rather than a sign-up even for a first visit: the server creates a missing
        // account silently and does not say which of the two happened.
        this.analytics.login('google');
        this.userService.setToken(token);

        this.userService.getAndInitUserInfo(this.emailFromToken(token)).subscribe({
            complete: () => {
                this.router.navigateByUrl(safeReturnUrl(returnUrl)).then(() => {
                    if (this.isBrowser) { window.location.reload(); }
                });
            }
        });
    }

    /// The profile lookup is keyed on email and the Google flow never asks for one, so
    /// read it out of the JWT the server just issued rather than trusting the browser.
    private emailFromToken(token: string): string {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress']
                || payload.email
                || '';
        } catch {
            return '';
        }
    }

    /**
     * The country the site worked out for this visitor, as the pair the API wants: the
     * ISO2 code, and the numeric id the registration form uses.
     *
     * The id only exists in the cached country list, so it is looked up by matching the
     * detected code against it. When the list has not loaded yet the id is left at the
     * US default rather than paired with a mismatched code - a code of BD with the id
     * for the US would be worse than an honest default.
     */
    private detectedCountry(): { country: number; countryCode: string } {
        const fallback = { country: 243, countryCode: 'US' };

        try {
            const code = this.store.countryInfo?.CountryCode
                || this.store.getCountryInfoFromStorage()?.CountryCode;

            if (!code) { return fallback; }

            const list = this.store.cultureState?.typeInfos
                ? this.store.cultureState.typeInfos['System.Country']
                : null;

            const match = list
                ? list.find(c => (c.ValueText || '').toLowerCase() === code.toLowerCase())
                : null;

            return match
                ? { country: Number(match.Value), countryCode: match.ValueText }
                : fallback;
        } catch {
            return fallback;
        }
    }

    private rememberHints(country: number, countryCode: string, language: number, returnUrl?: string): void {
        try {
            sessionStorage.setItem(GoogleAuthService.HintsKey,
                JSON.stringify({ country, countryCode, language, returnUrl: returnUrl || null }));
        } catch {
            // Private mode, or storage disabled. The server falls back to its own
            // defaults, so this is not worth failing the sign-in over.
        }
    }

    private readHints(): { country: number; countryCode: string; language: number; returnUrl?: string } {
        const fallback = { country: 243, countryCode: 'US', language: 22, returnUrl: null as string };

        try {
            const raw = sessionStorage.getItem(GoogleAuthService.HintsKey);
            sessionStorage.removeItem(GoogleAuthService.HintsKey);
            return raw ? { ...fallback, ...JSON.parse(raw) } : fallback;
        } catch {
            return fallback;
        }
    }
}
